import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Users, Award, CheckCircle2, XCircle, Download, Search, FilePlus2, UploadCloud, Loader2,
  ShieldCheck, TrendingUp, FileText, Building2, Ban, Check,
} from "lucide-react";

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function adminDownloadCert(certId) {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/certificate/${certId}/pdf`, {
    credentials: "include",
  });
  if (!res.ok) { toast.error("Download failed"); return; }
  const blob = await res.blob();
  downloadBlob(blob, `bbp-certificate-${certId}.pdf`);
}

function StatCard({ label, value, accent = "red" }) {
  const colors = {
    red: "border-red-600",
    emerald: "border-emerald-600",
    zinc: "border-zinc-900",
    amber: "border-amber-600",
  };
  return (
    <div className={`border-l-4 pl-4 ${colors[accent]}`}>
      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-display text-3xl font-black mt-1">{value}</div>
    </div>
  );
}

function StudentsTab({ students, totalModules, page, totalPages, total, pageSize, searchQ, onSearchChange, onPageChange }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={searchQ}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by name or email…"
            className="pl-9"
            data-testid="admin-student-search"
          />
        </div>
        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500" data-testid="admin-students-count">
          {total === 0
            ? "0 students"
            : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}
        </div>
      </div>

      <div className="border border-zinc-200 rounded-lg overflow-hidden bg-white">
        <div className="grid grid-cols-12 bg-zinc-50 border-b border-zinc-200 py-3 px-4 text-[11px] font-mono uppercase tracking-widest text-zinc-500">
          <div className="col-span-3">Student</div>
          <div className="col-span-2">Progress</div>
          <div className="col-span-2">Exam</div>
          <div className="col-span-1">Practice</div>
          <div className="col-span-4">Certificates</div>
        </div>
        <div className="divide-y divide-zinc-100" data-testid="admin-students-table">
          {students.length === 0 && (
            <div className="p-6 text-sm text-zinc-500">No students match your search.</div>
          )}
          {students.map((s) => (
            <div key={s.user_id} className="grid grid-cols-12 py-4 px-4 items-center text-sm" data-testid={`admin-student-row-${s.user_id}`}>
              <div className="col-span-3 min-w-0">
                <div className="font-medium truncate">{s.name || "—"}</div>
                <div className="text-xs text-zinc-500 truncate">{s.email}</div>
                <div className="text-[10px] font-mono uppercase text-zinc-400 mt-0.5">
                  {s.provider === "google" ? "Google" : "Email"} · joined {String(s.created_at).slice(0, 10)}
                </div>
              </div>
              <div className="col-span-2">
                <div className="font-mono font-bold text-lg">{s.progress.percent}%</div>
                <div className="text-xs text-zinc-500">{s.progress.completed_count}/{totalModules} modules</div>
              </div>
              <div className="col-span-2">
                {s.exam.attempted ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      {s.exam.passed ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-red-600" />}
                      <span className="font-mono font-bold">{s.exam.score}%</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 font-mono">
                      MCQ {s.exam.mcq_score ?? "—"}% · CS {s.exam.case_study_score ?? "—"}%
                    </div>
                  </>
                ) : (
                  <span className="text-xs text-zinc-400 font-mono">Not attempted</span>
                )}
              </div>
              <div className="col-span-1">
                <div className="font-mono text-sm">{s.practice.sessions}</div>
                <div className="text-[11px] text-zinc-500">best {s.practice.best_percent}%</div>
              </div>
              <div className="col-span-4 space-y-1">
                {(!s.certificates || s.certificates.length === 0) && (
                  <span className="text-xs text-zinc-400 font-mono">No certificates</span>
                )}
                {(s.certificates || []).map((c) => (
                  <div key={c.certificate_id} className="flex items-center justify-between gap-2 text-xs">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[11px] truncate" title={c.certificate_id}>{c.certificate_id}</div>
                      <div className="text-zinc-500 text-[10px] font-mono">
                        {c.score}% · {String(c.issued_at).slice(0, 10)} · {c.paid ? "PAID" : "UNPAID"}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => adminDownloadCert(c.certificate_id)}
                      data-testid={`admin-download-cert-${c.certificate_id}`}
                    >
                      <Download className="h-3 w-3 mr-1" /> PDF
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4" data-testid="admin-students-pagination">
          <Button
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            data-testid="admin-students-prev-btn"
          >
            ← Previous
          </Button>
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">
            Page {page} of {totalPages}
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            data-testid="admin-students-next-btn"
          >
            Next →
          </Button>
        </div>
      )}
    </div>
  );
}

function SingleCertTab({ onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const twoYears = new Date(Date.now() + 730 * 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    name: "", email: "", score: 85, issued_at: today, expires_at: twoYears,
  });
  const [busy, setBusy] = useState(false);
  const [lastCert, setLastCert] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/certificate/generate", {
        ...form,
        score: Number(form.score),
      });
      setLastCert(data);
      toast.success(`Certificate ${data.certificate_id} created`);
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create certificate");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-2xl space-y-5 bg-white border border-zinc-200 rounded-lg p-6" data-testid="admin-single-cert-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="cn">Name *</Label>
          <Input id="cn" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="admin-single-name" />
        </div>
        <div>
          <Label htmlFor="ce">Email (optional, for record only)</Label>
          <Input id="ce" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-single-email" />
        </div>
        <div>
          <Label htmlFor="cs">Score % *</Label>
          <Input id="cs" type="number" min={0} max={100} value={form.score} onChange={(e) => setForm({ ...form, score: e.target.value })} required data-testid="admin-single-score" />
        </div>
        <div />
        <div>
          <Label htmlFor="ci">Issued date</Label>
          <Input id="ci" type="date" value={form.issued_at} onChange={(e) => setForm({ ...form, issued_at: e.target.value })} data-testid="admin-single-issued" />
        </div>
        <div>
          <Label htmlFor="cx">Expires date</Label>
          <Input id="cx" type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="admin-single-expires" />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="admin-single-submit">
          {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating…</> : <><FilePlus2 className="mr-2 h-4 w-4" /> Generate certificate</>}
        </Button>
        {lastCert && (
          <Button type="button" variant="outline" onClick={() => adminDownloadCert(lastCert.certificate_id)} data-testid="admin-single-download">
            <Download className="mr-2 h-4 w-4" /> Download last ({lastCert.certificate_id.slice(-6)})
          </Button>
        )}
      </div>
    </form>
  );
}

function BulkCertTab({ onCreated }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const upload = async (e) => {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Choose a CSV file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/certificate/generate-bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReport(data);
      toast.success(`${data.created} certificates created (${data.failed} failed)`);
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk upload failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    const csv = "name,email,score,issued_at,expires_at\nJane Doe,jane@example.com,88,2026-01-15,2028-01-14\nJohn Smith,,92,,\n";
    downloadBlob(new Blob([csv], { type: "text/csv" }), "bbp-bulk-template.csv");
  };

  return (
    <div className="max-w-3xl space-y-5">
      <form onSubmit={upload} className="bg-white border border-zinc-200 rounded-lg p-6 space-y-4" data-testid="admin-bulk-form">
        <div>
          <div className="font-display font-bold text-lg mb-1">Bulk offline certificate generation</div>
          <div className="text-sm text-zinc-600">
            Upload a CSV with columns: <code className="font-mono text-xs bg-zinc-100 px-1 py-0.5 rounded">name, email, score, issued_at, expires_at</code>. Only <strong>name</strong> is required.
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="text-sm" data-testid="admin-bulk-file-input" />
          <Button type="button" variant="outline" size="sm" onClick={downloadTemplate} data-testid="admin-bulk-download-template">
            <FileText className="mr-2 h-3.5 w-3.5" /> CSV template
          </Button>
        </div>

        <Button type="submit" disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="admin-bulk-submit">
          {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…</> : <><UploadCloud className="mr-2 h-4 w-4" /> Upload &amp; generate</>}
        </Button>
      </form>

      {report && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="admin-bulk-report">
          <div className="flex items-center gap-3 mb-4">
            <StatCard label="Created" value={report.created} accent="emerald" />
            <StatCard label="Failed" value={report.failed} accent="red" />
          </div>

          {report.results.length > 0 && (
            <>
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Successful</div>
              <div className="divide-y divide-zinc-100 max-h-60 overflow-y-auto border border-zinc-200 rounded-md">
                {report.results.map((r) => (
                  <div key={r.certificate_id} className="grid grid-cols-12 items-center py-2 px-3 text-xs">
                    <div className="col-span-1 font-mono text-zinc-500">#{r.row}</div>
                    <div className="col-span-3 font-medium truncate">{r.name}</div>
                    <div className="col-span-3 truncate text-zinc-600">{r.email || "—"}</div>
                    <div className="col-span-1 font-mono">{r.score}%</div>
                    <div className="col-span-3 font-mono text-[10px] truncate" title={r.certificate_id}>{r.certificate_id}</div>
                    <div className="col-span-1 flex justify-end">
                      <Button size="sm" variant="ghost" onClick={() => adminDownloadCert(r.certificate_id)} className="h-6 w-6 p-0">
                        <Download className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {report.errors.length > 0 && (
            <>
              <div className="text-xs font-mono uppercase tracking-widest text-red-600 mt-4 mb-2">Errors</div>
              <div className="divide-y divide-red-100 max-h-40 overflow-y-auto border border-red-200 rounded-md bg-red-50">
                {report.errors.map((e, i) => (
                  <div key={i} className="py-2 px-3 text-xs">
                    <span className="font-mono text-red-700">Row #{e.row}:</span> {e.error}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CorporateOrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/corporate-orders");
      setOrders(data.orders || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const markPaid = async (ref) => {
    if (!window.confirm(`Mark order ${ref} as PAID and activate all its seats?`)) return;
    try {
      await api.post(`/admin/corporate-orders/${ref}/mark-paid`);
      toast.success("Order marked as paid — all seats activated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mark paid");
    }
  };

  const cancel = async (ref) => {
    if (!window.confirm(`Cancel order ${ref}? All unpaid seats will be deleted.`)) return;
    try {
      await api.post(`/admin/corporate-orders/${ref}/cancel`);
      toast.success("Order cancelled");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    }
  };

  const statusBadge = (status) => {
    const map = {
      paid: "bg-emerald-50 text-emerald-700",
      invoiced: "bg-amber-50 text-amber-700",
      created: "bg-blue-50 text-blue-700",
      cancelled: "bg-zinc-100 text-zinc-500",
      capture_failed: "bg-red-50 text-red-700",
    };
    return map[status] || "bg-zinc-100 text-zinc-500";
  };

  return (
    <div data-testid="admin-corporate-tab">
      {loading ? (
        <div className="text-sm text-zinc-500 font-mono">Loading orders…</div>
      ) : orders.length === 0 ? (
        <div className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-md p-8 text-center">
          No corporate orders yet.
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-lg overflow-hidden bg-white">
          <div className="grid grid-cols-12 bg-zinc-50 border-b border-zinc-200 py-3 px-4 text-[11px] font-mono uppercase tracking-widest text-zinc-500">
            <div className="col-span-3">Company</div>
            <div className="col-span-2">Seats</div>
            <div className="col-span-2">Total</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>
          <div className="divide-y divide-zinc-100" data-testid="admin-corp-orders-list">
            {orders.map((o) => (
              <div key={o.order_ref} className="grid grid-cols-12 py-4 px-4 items-center text-sm" data-testid={`admin-corp-row-${o.order_ref}`}>
                <div className="col-span-3 min-w-0">
                  <div className="font-medium truncate">{o.company_name}</div>
                  <div className="text-xs text-zinc-500 truncate">{o.contact_email}</div>
                  <div className="font-mono text-[10px] text-zinc-400 mt-0.5 truncate">{o.order_ref}</div>
                </div>
                <div className="col-span-2 font-mono">{o.seats}</div>
                <div className="col-span-2 font-mono font-bold">${o.total_usd?.toFixed?.(2) || o.total_usd}</div>
                <div className="col-span-2">
                  <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded ${statusBadge(o.status)}`}>
                    {o.status}
                  </span>
                  <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                    {o.payment_method === "invoice" ? "Invoice" : "PayPal"} · {String(o.created_at).slice(0, 10)}
                  </div>
                </div>
                <div className="col-span-3 flex items-center justify-end gap-1">
                  {o.payment_method === "invoice" && o.status === "invoiced" && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/corporate/invoice/${o.order_ref}/pdf`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center h-7 px-2 border border-zinc-300 rounded text-xs hover:bg-zinc-50"
                      data-testid={`admin-corp-invoice-${o.order_ref}`}
                    >
                      <FileText className="h-3 w-3 mr-1" /> Invoice
                    </a>
                  )}
                  <a
                    href={`/corporate/order/${o.order_ref}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center h-7 px-2 border border-zinc-300 rounded text-xs hover:bg-zinc-50"
                    data-testid={`admin-corp-roster-${o.order_ref}`}
                  >
                    Roster
                  </a>
                  {o.status !== "paid" && o.status !== "cancelled" && (
                    <>
                      <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => markPaid(o.order_ref)} data-testid={`admin-corp-markpaid-${o.order_ref}`}>
                        <Check className="h-3 w-3 mr-1" /> Mark paid
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs text-red-700 hover:bg-red-50" onClick={() => cancel(o.order_ref)} data-testid={`admin-corp-cancel-${o.order_ref}`}>
                        <Ban className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


export default function Admin() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [students, setStudents] = useState([]);
  const [totalModules, setTotalModules] = useState(13);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  const [totalStudents, setTotalStudents] = useState(0);
  const [searchQ, setSearchQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  // Debounce search input by 300ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchQ), 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  // When search changes, reset to page 1
  useEffect(() => {
    setPage(1);
  }, [debouncedQ]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (debouncedQ.trim()) params.set("q", debouncedQ.trim());
      const [s, st] = await Promise.all([
        api.get("/admin/summary"),
        api.get(`/admin/students?${params.toString()}`),
      ]);
      setSummary(s.data);
      setStudents(st.data.students);
      setTotalModules(st.data.total_modules || 13);
      setTotalPages(st.data.total_pages || 0);
      setTotalStudents(st.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") load();
     
  }, [user, page, debouncedQ]);

  if (user && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-6 w-6 text-red-600" />
          <div className="text-xs font-mono uppercase tracking-widest text-red-600">Admin portal</div>
        </div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight" data-testid="admin-heading">
          Students, progress &amp; certificates
        </h1>
        <p className="text-zinc-600 mt-2">Manage enrolled students and issue offline certificates.</p>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-6 mt-8 mb-10" data-testid="admin-summary-cards">
            <StatCard label="Students" value={summary.total_students} accent="zinc" />
            <StatCard label="Certificates" value={summary.total_certificates} accent="red" />
            <StatCard label="Paid" value={summary.paid_certificates} accent="emerald" />
            <StatCard label="Offline" value={summary.offline_certificates} accent="amber" />
            <StatCard label="Exam attempts" value={summary.exam_attempts} accent="zinc" />
            <StatCard label="Passed" value={summary.exam_passed} accent="emerald" />
          </div>
        )}

        <Tabs defaultValue="students">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="students" data-testid="tab-students"><Users className="h-4 w-4 mr-2" /> Students</TabsTrigger>
            <TabsTrigger value="single" data-testid="tab-single"><FilePlus2 className="h-4 w-4 mr-2" /> Single cert</TabsTrigger>
            <TabsTrigger value="bulk" data-testid="tab-bulk"><UploadCloud className="h-4 w-4 mr-2" /> Bulk upload</TabsTrigger>
            <TabsTrigger value="corporate" data-testid="tab-corporate"><Building2 className="h-4 w-4 mr-2" /> Corporate</TabsTrigger>
          </TabsList>

          <TabsContent value="students" className="mt-6">
            {loading ? (
              <div className="text-sm text-zinc-500 font-mono">Loading students…</div>
            ) : (
              <StudentsTab
                students={students}
                totalModules={totalModules}
                page={page}
                totalPages={totalPages}
                total={totalStudents}
                pageSize={pageSize}
                searchQ={searchQ}
                onSearchChange={setSearchQ}
                onPageChange={setPage}
              />
            )}
          </TabsContent>

          <TabsContent value="single" className="mt-6">
            <SingleCertTab onCreated={load} />
          </TabsContent>

          <TabsContent value="bulk" className="mt-6">
            <BulkCertTab onCreated={load} />
          </TabsContent>

          <TabsContent value="corporate" className="mt-6">
            <CorporateOrdersTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
